import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { APP_URL, LANDING_URL, isOnAppHost, isOnLandingHost } from './urls';

/**
 * Routes allowed on the public landing hostname (condorsecurity.co).
 * Everything else must live on `app.condorsecurity.co`.
 */
const LANDING_ALLOWED_PREFIXES = ['/free-tools', '/reports/shared/', '/trust/'];

function isLandingAllowed(pathname: string): boolean {
  if (pathname === '/' || pathname === '') return true;
  return LANDING_ALLOWED_PREFIXES.some((prefix) => pathname.startsWith(prefix));
}

/**
 * Enforces the split-hostname contract:
 * - On the public landing (`condorsecurity.co`), only landing routes render.
 *   Any other path hops to `app.condorsecurity.co<path>`.
 * - On the app host (`app.condorsecurity.co`), hitting `/` bounces to landing.
 *
 * In dev (APP_URL === LANDING_URL === http://localhost:3000) both origins
 * resolve the same — `isOnAppHost()` and `isOnLandingHost()` are both true
 * and no redirect fires, so the SPA works as today.
 */
export function useHostnameGuard(): void {
  const location = useLocation();

  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Dev / same-origin: no split to enforce.
    if (APP_URL === LANDING_URL) return;

    // Customer trust-center domains are neither app nor landing: they render
    // their own catch-all (see App.tsx) and must never be redirected.
    if (!isOnAppHost() && !isOnLandingHost()) return;

    const path = location.pathname + location.search + location.hash;

    if (isOnLandingHost() && !isOnAppHost()) {
      if (!isLandingAllowed(location.pathname)) {
        window.location.replace(`${new URL(APP_URL).origin}${path}`);
      }
      return;
    }

    if (isOnAppHost() && !isOnLandingHost()) {
      if (location.pathname === '/') {
        window.location.replace(`${new URL(LANDING_URL).origin}/`);
      }
    }
  }, [location.pathname, location.search, location.hash]);
}
